import fs from 'node:fs/promises'
import os from 'node:os'
import path from 'node:path'
import memoize from 'fast-memoize'
import { parse as parseYaml } from 'yaml'
import exists from '../lib/exists.ts'
import findLockfile from '../lib/findLockfile.ts'
import interpolate from '../lib/interpolate.ts'
import { keyValueBy } from '../lib/keyValueBy.ts'
import { print } from '../lib/logging.ts'
import parseCooldown from '../lib/parseCooldown.ts'
import spawnCommand from '../lib/spawnCommand.ts'
import { type GetVersion } from '../types/GetVersion.ts'
import { type Index } from '../types/IndexType.ts'
import { type NativeCooldown } from '../types/NativeCooldown.ts'
import { type NpmConfig } from '../types/NpmConfig.ts'
import { type NpmOptions } from '../types/NpmOptions.ts'
import { type Options } from '../types/Options.ts'
import { type SpawnOptions } from '../types/SpawnOptions.ts'
import { type SpawnPleaseOptions } from '../types/SpawnPleaseOptions.ts'
import { type SpawnResult } from '../types/SpawnResult.ts'
import { type Version } from '../types/Version.ts'
import { type VersionSpec } from '../types/VersionSpec.ts'
import * as npm from './npm.ts'

interface ParsedDep {
  version: string
  from: string
  required?: {
    version: string
  }
}

export interface NpmScope {
  npmAlwaysAuth?: boolean
  npmAuthToken?: string
  npmRegistryServer?: string
}

interface YarnConfig {
  npmScopes?: Index<NpmScope>
  npmMinimalAgeGate?: number | string
  npmPreapprovedPackages?: string[]
}

/** Shape of the yarn .yarnrc.yml npmMinimalAgeGate settings. */
export interface YarnMinimalAgeGate {
  /** Minimum release age in minutes (yarn's native unit). */
  npmMinimalAgeGate: number
  /** List of package names excluded from the age gate check. */
  npmPreapprovedPackages: string[]
}

/** Reads an auth token from a yarn config, interpolates it, and returns it as an npm config key-value pair. */
export const npmAuthTokenKeyValue = (npmConfig: Index<string | boolean>) => (dep: string, scopedConfig: NpmScope) => {
  if (scopedConfig.npmAuthToken) {
    // get registry server from this config or a previous config (assumes setNpmRegistry has already been called on all npm scopes)
    // interpolate like the registry value, or an unexpanded ${VAR} yields an auth key that never matches
    const registryServer = scopedConfig.npmRegistryServer
      ? interpolate(scopedConfig.npmRegistryServer, process.env)
      : (npmConfig[`@${dep}:registry`] as string | undefined)
    // interpolate environment variable fallback
    // https://yarnpkg.com/configuration/yarnrc
    if (registryServer) {
      let trimmedRegistryServer = registryServer.replace(/^https?:/, '')

      if (trimmedRegistryServer.endsWith('/')) {
        trimmedRegistryServer = trimmedRegistryServer.slice(0, -1)
      }

      return {
        [`${trimmedRegistryServer}/:_authToken`]: interpolate(scopedConfig.npmAuthToken, process.env),
      }
    }
  }

  return null
}

/** Reads a registry from a yarn config. interpolates it, and returns it as an npm config key-value pair. */
const npmRegistryKeyValue = (dep: string, scopedConfig: NpmScope): null | Index<VersionSpec> =>
  scopedConfig.npmRegistryServer
    ? { [`@${dep}:registry`]: interpolate(scopedConfig.npmRegistryServer, process.env) }
    : null

/**
 * Returns the path to the local .yarnrc.yml, or undefined. This doesn't
 * actually check that the .yarnrc.yml file exists.
 *
 * Exported for test purposes only.
 *
 * @param readdir This is only a parameter so that it can be used in tests.
 */
export async function getPathToLookForYarnrc(
  options: Options,
  readdir: (_path: string) => Promise<string[]> = fs.readdir,
): Promise<string | undefined> {
  if (options.global) return undefined

  const directoryPath = (await findLockfile(options, readdir))?.directoryPath
  if (!directoryPath) return undefined

  return path.join(directoryPath, '.yarnrc.yml')
}

// If private registry auth is specified in npmScopes in .yarnrc.yml, read them in and convert them to npm config variables.
// Define as a memoized function to efficiently call exists and readFile only once, and only if yarn is being used.
// https://github.com/raineorshine/npm-check-updates/issues/1036
const npmConfigFromYarn = memoize(async (options: Options): Promise<NpmConfig> => {
  const yarnrcLocalPath = await getPathToLookForYarnrc(options)
  const yarnrcUserPath = path.join(os.homedir(), '.yarnrc.yml')
  const yarnrcLocalExists = typeof yarnrcLocalPath === 'string' && (await exists(yarnrcLocalPath))
  const yarnrcUserExists = await exists(yarnrcUserPath)
  const yarnrcLocal = yarnrcLocalExists ? await fs.readFile(yarnrcLocalPath, 'utf-8') : ''
  const yarnrcUser = yarnrcUserExists ? await fs.readFile(yarnrcUserPath, 'utf-8') : ''
  const yarnConfigLocal: YarnConfig = parseYaml(yarnrcLocal)
  const yarnConfigUser: YarnConfig = parseYaml(yarnrcUser)

  let npmConfig: Index<string | boolean> = {
    ...keyValueBy(yarnConfigUser?.npmScopes || {}, npmRegistryKeyValue),
    ...keyValueBy(yarnConfigLocal?.npmScopes || {}, npmRegistryKeyValue),
  }

  // npmAuthTokenKeyValue uses scoped npmRegistryServer, so must come after npmRegistryKeyValue
  npmConfig = {
    ...npmConfig,
    ...keyValueBy(yarnConfigUser?.npmScopes || {}, npmAuthTokenKeyValue(npmConfig)),
    ...keyValueBy(yarnConfigLocal?.npmScopes || {}, npmAuthTokenKeyValue(npmConfig)),
  }

  // set auth token after npm registry, since auth token syntax uses registry

  if (yarnrcLocalExists) {
    print(options, `\nUsing local yarn config at ${yarnrcLocalPath}:`, 'verbose')
    print(options, yarnConfigLocal, 'verbose')
  }
  if (yarnrcUserExists) {
    print(options, `\nUsing user yarn config at ${yarnrcUserPath}:`, 'verbose')
    print(options, yarnConfigUser, 'verbose')
  }

  if (Object.keys(npmConfig).length > 0) {
    print(options, '\nMerged yarn config in npm format:', 'verbose')
    print(options, npmConfig, 'verbose')
  }

  return npmConfig
})

/** Reads npmMinimalAgeGate settings from .yarnrc.yml if present. Checks local config first, then user config. */
const getYarnMinimalAgeGate = memoize(async (options: Options): Promise<YarnMinimalAgeGate | null> => {
  const yarnrcLocalPath = await getPathToLookForYarnrc(options)
  const yarnrcUserPath = path.join(os.homedir(), '.yarnrc.yml')

  for (const yarnrcPath of [yarnrcLocalPath, yarnrcUserPath]) {
    if (!yarnrcPath) continue
    if (!(await exists(yarnrcPath))) continue

    let content: string
    try {
      content = await fs.readFile(yarnrcPath, 'utf-8')
    } catch {
      continue
    }

    let parsed: YarnConfig
    try {
      parsed = parseYaml(content) ?? {}
    } catch {
      continue
    }

    const { npmMinimalAgeGate: rawNpmMinimalAgeGate } = parsed
    let npmMinimalAgeGate: number
    if (typeof rawNpmMinimalAgeGate === 'number') {
      if (isNaN(rawNpmMinimalAgeGate) || rawNpmMinimalAgeGate <= 0) continue
      npmMinimalAgeGate = rawNpmMinimalAgeGate
    } else if (typeof rawNpmMinimalAgeGate === 'string') {
      const days = parseCooldown(rawNpmMinimalAgeGate)
      if (days === null || days <= 0) continue
      const MINUTES_PER_DAY = 24 * 60
      npmMinimalAgeGate = days * MINUTES_PER_DAY
    } else {
      continue
    }

    const rawPreapproved = parsed.npmPreapprovedPackages
    const npmPreapprovedPackages: string[] = Array.isArray(rawPreapproved)
      ? rawPreapproved.filter((x): x is string => typeof x === 'string')
      : []

    return { npmMinimalAgeGate, npmPreapprovedPackages }
  }

  return null
})

/**
 * Parse JSON lines and throw an informative error on failure.
 *
 * Note: although this is similar to the npm parseJson() function we always return the
 * same concrete-type here, for now.
 *
 * @param result    Output from `yarn list --json` to be parsed
 */
function parseJsonLines(result: string): { dependencies: Index<ParsedDep> } {
  const dependencies: Index<ParsedDep> = {}

  for (const line of result.split('\n')) {
    if (!line.trim()) continue

    const d = JSON.parse(line)

    // only parse info data
    // ignore error info, e.g. "Visit https://yarnpkg.com/en/docs/cli/list for documentation about this command."
    if (d.type === 'info' && !d.data.match(/^Visit/)) {
      // parse package name and version number from info data, e.g. "nodemon@2.0.4" has binaries
      const [, pkgName, pkgVersion] = d.data.match(/"(@?[^@]*)@([^"]*)"/) || []
      if (!pkgName) continue

      dependencies[pkgName] = {
        version: pkgVersion,
        from: pkgName,
      }
    } else if (d.type === 'error') {
      throw new Error(d.data)
    }
  }

  return { dependencies }
}

/**
 * Extract first json line from multi-line yarn output.
 *
 * @param result    Output from yarn command to be parsed
 */
function extractFirstJsonLine(result: string): string {
  for (const line of result.split('\n')) {
    if (!line.trim()) continue
    return JSON.stringify(JSON.parse(line))
  }

  throw new Error('No JSON line found')
}

/**
 * Spawn yarn requires a different command on Windows.
 *
 * @param args
 * @param [yarnOptions={}]
 * @param [spawnOptions={}]
 * @returns
 */
async function spawnYarn(
  args: string | string[],
  yarnOptions: NpmOptions = {},
  spawnPleaseOptions: SpawnPleaseOptions = {},
  spawnOptions: SpawnOptions = {},
): Promise<SpawnResult> {
  const fullArgs = [
    ...(yarnOptions.global ? ['global'] : []),
    ...(yarnOptions.prefix ? [`--prefix=${yarnOptions.prefix}`] : []),
    '--depth=0',
    '--json',
    '--no-progress',
    // args must go after yarn options; otherwise, they are passed through to npm scripts
    // https://github.com/raineorshine/npm-check-updates/issues/1362
    ...(Array.isArray(args) ? args : [args]),
  ]

  return spawnCommand('yarn', fullArgs, spawnPleaseOptions, spawnOptions)
}

/**
 * Get platform-specific default prefix to pass on to yarn.
 *
 * @param options
 * @param [options.global]
 * @param [options.prefix]
 * @returns
 */
export async function defaultPrefix(options: Options): Promise<string | null> {
  if (options.prefix) {
    return options.prefix
  }

  // yarn 2.0 does not support yarn global, so catch the error to prevent the process from crashing
  // https://github.com/raineorshine/npm-check-updates/issues/873
  let prefix: string | null
  try {
    prefix = (await spawnCommand('yarn', ['global', 'dir'])).stdout
  } catch {
    prefix = null
  }

  // FIX: for ncu -g doesn't work on homebrew or windows #146
  // https://github.com/raineorshine/npm-check-updates/issues/146

  return options.global && prefix && prefix.match('Cellar')
    ? '/usr/local'
    : // Workaround: get prefix on windows for global packages
      // Only needed when using npm api directly
      process.platform === 'win32' && options.global && !process.env.prefix
      ? prefix
        ? prefix.trim()
        : `${process.env.LOCALAPPDATA}\\Yarn\\Data\\global`
      : null
}

/**
 * Fetches the list of all installed packages.
 *
 * @param [options]
 * @param [options.cwd]
 * @param [options.global]
 * @param [options.prefix]
 * @returns
 */
export const list = async (options: Options = {}, spawnOptions?: SpawnOptions): Promise<Index<string | undefined>> => {
  const { stdout: jsonLines } = await spawnYarn(
    'list',
    options as NpmOptions,
    {},
    {
      ...(options.cwd ? { cwd: options.cwd } : {}),
      ...spawnOptions,
    },
  )
  const json: { dependencies: Index<ParsedDep> } = parseJsonLines(jsonLines)
  const keyValues: Index<string | undefined> = keyValueBy<ParsedDep, string | undefined>(
    json.dependencies,
    (name, info): { [key: string]: string | undefined } => ({
      // unmet peer dependencies have a different structure
      [name]: info.version || info.required?.version,
    }),
  )
  return keyValues
}

/** Wraps a GetVersion function and passes the yarn config. */
const withNpmConfigFromYarn =
  (getVersion: GetVersion): GetVersion =>
  async (packageName, currentVersion, options = {}) =>
    getVersion(packageName, currentVersion, options, await npmConfigFromYarn(options))

export const distTag = withNpmConfigFromYarn(npm.distTag)
export const greatest = withNpmConfigFromYarn(npm.greatest)
export const latest = withNpmConfigFromYarn(npm.latest)
export const minor = withNpmConfigFromYarn(npm.minor)
export const newest = withNpmConfigFromYarn(npm.newest)
export const patch = withNpmConfigFromYarn(npm.patch)
export const semver = withNpmConfigFromYarn(npm.semver)

// memoized since getPeerDependencies is called once per dependency and the yarn version cannot change mid-run
const getYarnVersion = memoize(async (spawnOptions: SpawnOptions): Promise<string> => {
  const { stdout } = await spawnCommand('yarn', ['--version'], { rejectOnError: false }, spawnOptions)
  return stdout
})

/**
 * Fetches the list of peer dependencies for a specific package version.
 *
 * @param packageName
 * @param version
 * @param [options]
 * @param [spawnOptions]
 * @returns Promised {packageName: version} collection
 */
export const getPeerDependencies = async (
  packageName: string,
  version: Version,
  options: Options = {},
  spawnOptions?: SpawnOptions,
): Promise<Index<Version>> => {
  const yarnSpawnOptions: SpawnOptions = { cwd: options.cwd, ...spawnOptions }
  const yarnVersion = await getYarnVersion(yarnSpawnOptions)
  if (yarnVersion.startsWith('1')) {
    const args = ['--json', 'info', `${packageName}@${version}`, 'peerDependencies']
    const { stdout } = await spawnCommand('yarn', args, { rejectOnError: false }, yarnSpawnOptions)
    // yarn exits 0 and prints nothing when the package or version cannot be resolved, while a
    // package with no peer dependencies still prints an inspect line
    if (!stdout) throw new Error(`No response from "yarn ${args.join(' ')}"`)
    return npm.parseJson<{ data?: Index<Version> }>(stdout, { command: args.join(' ') }).data || {}
  } else {
    const args = ['--json', 'npm', 'info', `${packageName}@${version}`, '--fields', 'peerDependencies']
    const { stdout } = await spawnCommand('yarn', args, { rejectOnError: false }, yarnSpawnOptions)
    if (!stdout) {
      return {}
    }
    try {
      return (
        npm.parseJson<{ peerDependencies?: Index<Version> }>(stdout, { command: args.join(' ') }).peerDependencies || {}
      )
    } catch (parseError) {
      /*
      If package does not exist, yarn returns multiple json errors. As such, we want to extract just the first one, instead of crashing.
      Example response:
      {"type":"error","name":35,"displayName":"YN0035","indent":"","data":"Package not found"}
{"type":"error","name":35,"displayName":"YN0035","indent":"","data":"  \u001b[96mResponse Code\u001b[39m: \u001b]8;;https://developer.mozilla.org/en-US/docs/Web/HTTP/Status/404\u0007\u001b[93m404\u001b[39m (Not Found)\u001b]8;;\u0007"}
{"type":"error","name":35,"displayName":"YN0035","indent":"","data":"  \u001b[96mRequest Method\u001b[39m: GET"}
{"type":"error","name":35,"displayName":"YN0035","indent":"","data":"  \u001b[96mRequest URL\u001b[39m: \u001b[95mhttps://registry.yarnpkg.com/fffffffffffff\u001b[39m"}
       */
      let first: { type?: string; data?: string; peerDependencies?: Index<Version> } | undefined
      try {
        first = npm.parseJson(extractFirstJsonLine(stdout), { command: args.join(' ') })
      } catch {}

      if (!first) throw parseError
      // report the lookup as failed rather than as a package with no peer dependencies
      if (first.type === 'error') {
        throw new Error(`yarn ${args.join(' ')}: ${first.data ?? 'lookup failed'}`, { cause: parseError })
      }
      return first.peerDependencies || {}
    }
  }
}

/**
 * Fetches all dist-tags published for a package.
 *
 * @param packageName
 * @returns Promised {tag: version} collection
 */
export const getDistTags = async (packageName: string, options: Options = {}): Promise<Index<Version>> =>
  npm.getDistTags(packageName, options, await npmConfigFromYarn(options))

/**
 * Fetches the engines list from the registry for a specific package version.
 *
 * @param packageName
 * @param version
 * @returns Promised engines collection
 */
export const getEngines = async (
  packageName: string,
  version: Version,
  options: Options = {},
): Promise<Index<VersionSpec | undefined>> =>
  npm.getEngines(packageName, version, options, await npmConfigFromYarn(options))

/**
 * Check if package author changed between current and upgraded version.
 *
 * @param packageName Name of the package
 * @param currentVersion Current version declaration (may be range)
 * @param upgradedVersion Upgraded version declaration (may be range)
 * @param npmConfigLocal Additional npm config variables that are merged into the system npm config
 * @returns A promise that fulfills with boolean value.
 */
export const packageAuthorChanged = async (
  packageName: string,
  currentVersion: VersionSpec,
  upgradedVersion: VersionSpec,
  options: Options = {},
): Promise<boolean> =>
  npm.packageAuthorChanged(packageName, currentVersion, upgradedVersion, options, await npmConfigFromYarn(options))

export const usesAddCommand = true

export { spawnYarn as spawn }

export default spawnYarn

export const yarnApi = {
  getYarnMinimalAgeGate,
  parseJsonLines,
  extractFirstJsonLine,
}

/**
 * Reads yarn's npmMinimalAgeGate as a cooldown.
 * yarn does not read .npmrc min-release-age; only its own native config is consulted.
 */
export const getCooldown = async (options: Options): Promise<NativeCooldown | null> => {
  const config = await getYarnMinimalAgeGate(options)
  if (config == null) return null
  return {
    // yarn's npmMinimalAgeGate is in minutes
    days: config.npmMinimalAgeGate / (24 * 60),
    exclude: config.npmPreapprovedPackages,
    source: 'npmMinimalAgeGate from .yarnrc.yml',
    excludeLabel: 'pre-approved package',
  }
}
